<template>
  <v-menu
      v-model="menu"
      :close-on-content-click="false"
      :disabled="disabled"
      :open-on-click="false"
      max-width="320"
      min-width="320"
      offset-y
      transition="scale-transition"
  >
    <template v-slot:activator="{ attrs }">
      <v-text-field
          v-model="inputValue"
          :disabled="disabled"
          :placeholder="placeholder"
          class="year-picker-field"
          hide-details
          prepend-inner-icon="mdi-calendar"
          solo
          v-bind="attrs"
          @click:prepend-inner.stop="openMenu"
          @blur="onBlur"
          @input="onTextInput"
          @keydown.enter.prevent="onEnter"
      ></v-text-field>
    </template>
    <v-date-picker
        v-model="pickerValue"
        :max="pickerMax"
        :min="pickerMin"
        no-title
        scrollable
        @input="onPick"
    ></v-date-picker>
  </v-menu>
</template>

<script>
import {
  normalizeBirthDateInput,
  parseBirthDateInput,
} from "@/utils/birth-date-input";

export default {
  name: "YearPickerField",
  props: {
    value: {
      type: [String, Number],
      default: null,
    },
    minYear: {
      type: [String, Number],
      default: null,
    },
    maxYear: {
      type: [String, Number],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "Select year",
    },
  },
  data() {
    return {
      inputValue: "",
      menu: false,
      pickerValue: null,
    };
  },
  computed: {
    pickerMin() {
      const year = this.normalizeYear(this.minYear);
      return year ? `${year}-01-01` : null;
    },
    pickerMax() {
      const year = this.normalizeYear(this.maxYear);
      return year ? `${year}-12-31` : null;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.syncPickerValue(value);
      },
    },
    menu(value) {
      if (value) {
        this.syncPickerValue(this.value);
      }
    },
  },
  methods: {
    normalizeYear(value) {
      const normalizedValue = `${value ?? ""}`.trim();
      return /^\d{4}$/.test(normalizedValue) ? normalizedValue : null;
    },
    parseInput(value) {
      const year = this.normalizeYear(value);
      if (year) {
        return {
          normalizedValue: year,
          year,
          isValid: true,
        };
      }

      const normalizedValue = normalizeBirthDateInput(value);
      if (!normalizedValue) {
        return {
          normalizedValue: "",
          year: null,
          isValid: false,
        };
      }

      const parsedDate = parseBirthDateInput(normalizedValue);
      if (!parsedDate.isValid || !parsedDate.isoDate) {
        return {
          normalizedValue,
          year: null,
          isValid: false,
        };
      }

      return {
        normalizedValue: parsedDate.isoDate,
        year: parsedDate.isoDate.slice(0, 4),
        isValid: true,
      };
    },
    syncPickerValue(value) {
      const parsedInput = this.parseInput(value);
      const fallbackYear = this.normalizeYear(this.maxYear)
          || new Date().getFullYear().toString();
      const year = parsedInput.year || fallbackYear;
      this.inputValue = parsedInput.year || `${value ?? ""}`.trim();
      this.pickerValue = `${year}-01-01`;
    },
    openMenu() {
      if (!this.disabled) {
        this.menu = true;
      }
    },
    emitParsedValue(value) {
      const parsedInput = this.parseInput(value);
      if (!parsedInput.normalizedValue) {
        this.$emit("input", null);
        this.$emit("change", null);
        return parsedInput;
      }

      const emittedValue = parsedInput.isValid
          ? parsedInput.year
          : parsedInput.normalizedValue;
      this.$emit("input", emittedValue);
      this.$emit("change", emittedValue);
      return parsedInput;
    },
    onTextInput(value) {
      this.inputValue = value;
      this.emitParsedValue(value);
    },
    onBlur() {
      const parsedInput = this.emitParsedValue(this.inputValue);
      this.inputValue = parsedInput.isValid
          ? parsedInput.year
          : parsedInput.normalizedValue;
    },
    onEnter() {
      this.onBlur();
    },
    onPick(value) {
      const year = this.normalizeYear((value || "").slice(0, 4));
      this.inputValue = year || "";
      this.$emit("input", year);
      this.$emit("change", year);
      this.menu = false;
    },
  },
};
</script>

<style scoped>
.year-picker-field {
  cursor: pointer;
}
</style>
